import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { adminApi } from '../api/admin.js';
import { ApiError } from '../api/client.js';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';

const TYPE_LABELS = {
    full_day:           'Full day',
    half_day_morning:   'Half-day · morning',
    half_day_afternoon: 'Half-day · afternoon',
};

const TYPE_OPTIONS = Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }));

export default function AdminHolidaysPage() {
    const queryClient = useQueryClient();
    const currentYear = DateTime.now().setZone('Asia/Karachi').year;
    const [year, setYear] = useState(currentYear);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['admin', 'holidays', year],
        queryFn: () => adminApi.listHolidays(year),
    });

    const [modal, setModal] = useState({ open: false, mode: 'add', holiday: null });
    const [confirm, setConfirm] = useState({ open: false });
    const [flash, setFlash] = useState(null);

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'holidays'] });

    const handleDelete = (h) => setConfirm({
        open: true,
        title: 'Delete holiday?',
        body: `“${h.name}” on ${h.date} will be removed. Existing attendance rows are unaffected. This cannot be undone.`,
        confirmLabel: 'Delete',
        confirmTone: 'danger',
        action: async () => {
            try {
                await adminApi.deleteHoliday(h.id);
                setFlash({ tone: 'success', text: `Deleted ${h.name}.` });
                invalidate();
            } catch (err) {
                setFlash({ tone: 'error', text: err.message || 'Could not delete holiday.' });
            }
        },
    });

    return (
        <>
            <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
                <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>Holidays</h1>
                <button
                    className="btn btn-primary"
                    onClick={() => setModal({ open: true, mode: 'add', holiday: null })}
                >
                    + Add holiday
                </button>
            </div>

            <YearSelector year={year} setYear={setYear} currentYear={currentYear} />

            {flash && (
                <FlashBanner flash={flash} onDismiss={() => setFlash(null)} />
            )}

            <div className="surface-card" style={{ padding: 0, overflow: 'hidden' }}>
                {isLoading && (
                    <p style={{ padding: 18, fontSize: 13, color: 'var(--color-text-secondary)' }}>
                        Loading…
                    </p>
                )}
                {isError && (
                    <p style={{ padding: 18, fontSize: 13, color: 'var(--color-danger)' }}>
                        Could not load holidays. {error?.message}
                    </p>
                )}
                {data && data.holidays.length === 0 && (
                    <p style={{ padding: 18, fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                        No holidays in {year} yet. Click “Add holiday” to add one.
                    </p>
                )}
                {data && data.holidays.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: 'var(--color-bg-secondary)' }}>
                                <Th>Date</Th>
                                <Th>Day</Th>
                                <Th>Name</Th>
                                <Th>Type</Th>
                                <Th style={{ width: 160 }}> </Th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.holidays.map((h) => {
                                const dt = DateTime.fromISO(h.date);
                                return (
                                    <tr key={h.id} style={{
                                        borderTop: '0.5px solid var(--color-border-tertiary)',
                                    }}>
                                        <Td className="tnum">{h.date}</Td>
                                        <Td style={{ color: 'var(--color-text-secondary)' }}>
                                            {dt.toFormat('EEEE')}
                                        </Td>
                                        <Td style={{ fontWeight: 500 }}>{h.name}</Td>
                                        <Td>{TYPE_LABELS[h.type] || h.type}</Td>
                                        <Td style={{ textAlign: 'right' }}>
                                            <button
                                                onClick={() => setModal({ open: true, mode: 'edit', holiday: h })}
                                                className="btn btn-soft"
                                                style={{ fontSize: 12, padding: '4px 10px', marginRight: 6 }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(h)}
                                                className="btn"
                                                style={{
                                                    fontSize: 12, padding: '4px 10px',
                                                    background: 'var(--color-pill-rejected-bg)',
                                                    color: 'var(--color-danger)',
                                                    border: '0.5px solid var(--color-danger-border)',
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {modal.open && (
                <HolidayModal
                    mode={modal.mode}
                    holiday={modal.holiday}
                    onCancel={() => setModal({ open: false })}
                    onSaved={(msg) => {
                        setModal({ open: false });
                        setFlash({ tone: 'success', text: msg });
                        invalidate();
                    }}
                />
            )}

            <ConfirmDialog
                open={confirm.open}
                title={confirm.title}
                body={confirm.body}
                confirmLabel={confirm.confirmLabel}
                confirmTone={confirm.confirmTone}
                onConfirm={async () => {
                    const action = confirm.action;
                    setConfirm({ open: false });
                    if (action) await action();
                }}
                onCancel={() => setConfirm({ open: false })}
            />
        </>
    );
}

function YearSelector({ year, setYear, currentYear }) {
    // Show: prev year, current year, next year + free type-in
    const years = useMemo(() => {
        const set = new Set([currentYear - 1, currentYear, currentYear + 1, year]);
        return [...set].sort((a, b) => a - b);
    }, [year, currentYear]);
    return (
        <div className="flex items-center" style={{ gap: 8, marginBottom: 14, fontSize: 13 }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>Year:</span>
            {years.map((y) => (
                <button
                    key={y}
                    onClick={() => setYear(y)}
                    className="btn"
                    style={{
                        padding: '4px 12px',
                        fontSize: 12,
                        fontWeight: y === year ? 500 : 400,
                        background: y === year ? 'var(--color-bg-info)' : 'var(--color-bg-primary)',
                        color: y === year ? 'var(--color-text-info)' : 'var(--color-text-secondary)',
                        border: y === year
                            ? '0.5px solid var(--color-border-info)'
                            : '0.5px solid var(--color-border-tertiary)',
                    }}
                >
                    {y}
                </button>
            ))}
        </div>
    );
}

function FlashBanner({ flash, onDismiss }) {
    const colors = flash.tone === 'success'
        ? {
            bg: 'var(--color-pill-approved-bg)',
            fg: 'var(--color-pill-approved-fg)',
            border: '0.5px solid rgba(8, 80, 65, 0.18)',
        }
        : {
            bg: 'var(--color-pill-rejected-bg)',
            fg: 'var(--color-danger)',
            border: '0.5px solid var(--color-danger-border)',
        };
    return (
        <div
            style={{
                marginBottom: 14, padding: '8px 12px',
                fontSize: 13, borderRadius: 'var(--border-radius-md)',
                background: colors.bg, color: colors.fg, border: colors.border,
            }}
        >
            {flash.text}
            <button
                onClick={onDismiss}
                style={{
                    marginLeft: 12, fontSize: 11, textDecoration: 'underline',
                    color: 'inherit', background: 'transparent', cursor: 'pointer', border: 'none',
                }}
            >
                dismiss
            </button>
        </div>
    );
}

function HolidayModal({ mode, holiday, onCancel, onSaved }) {
    const isEdit = mode === 'edit';
    const [date, setDate] = useState(holiday?.date || '');
    const [name, setName] = useState(holiday?.name || '');
    const [type, setType] = useState(holiday?.type || 'full_day');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!isEdit && !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            setError('Date is required.'); return;
        }
        if (name.trim().length === 0) {
            setError('Name is required.'); return;
        }
        setSubmitting(true);
        try {
            if (isEdit) {
                await adminApi.updateHoliday(holiday.id, { name: name.trim(), type });
                onSaved(`Updated ${name.trim()}.`);
            } else {
                await adminApi.createHoliday({ date, name: name.trim(), type });
                onSaved(`Added ${name.trim()}.`);
            }
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Could not save holiday.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 16,
            }}
        >
            <form
                onSubmit={submit}
                className="surface-card"
                style={{ padding: 22, width: '100%', maxWidth: 460 }}
            >
                <h2 style={{ fontSize: 15, fontWeight: 500, margin: 0, marginBottom: 14 }}>
                    {isEdit ? 'Edit holiday' : 'Add holiday'}
                </h2>

                <Field label="Date">
                    {isEdit ? (
                        <input className="input" type="text" value={date} disabled />
                    ) : (
                        <input
                            className="input" type="date" required
                            value={date} onChange={(e) => setDate(e.target.value)} autoFocus
                        />
                    )}
                    {isEdit && (
                        <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                            Date is fixed once a holiday is created. To move it, delete and re-add.
                        </p>
                    )}
                </Field>

                <Field label="Name">
                    <input
                        className="input" type="text" required maxLength={120}
                        placeholder="e.g. Labour Day"
                        value={name} onChange={(e) => setName(e.target.value)}
                        autoFocus={isEdit}
                    />
                </Field>

                <Field label="Type">
                    <select
                        className="select"
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                    >
                        {TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </Field>

                {error && (
                    <p
                        style={{
                            fontSize: 13, color: 'var(--color-danger)',
                            background: 'var(--color-pill-rejected-bg)',
                            border: '0.5px solid var(--color-danger-border)',
                            borderRadius: 'var(--border-radius-md)',
                            padding: '8px 12px', marginBottom: 12,
                        }}
                    >
                        {error}
                    </p>
                )}

                <div className="flex justify-end" style={{ gap: 8, paddingTop: 8, borderTop: '0.5px solid var(--color-border-tertiary)' }}>
                    <button type="button" onClick={onCancel} className="btn btn-ghost">Cancel</button>
                    <button type="submit" disabled={submitting} className="btn btn-primary">
                        {submitting ? 'Saving…' : (isEdit ? 'Save changes' : 'Add holiday')}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Th({ children, style }) {
    return (
        <th style={{
            textAlign: 'left',
            padding: '10px 14px',
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            ...style,
        }}>{children}</th>
    );
}

function Td({ children, style, className }) {
    return (
        <td className={className} style={{ padding: '10px 14px', ...style }}>{children}</td>
    );
}

function Field({ label, children }) {
    return (
        <div style={{ marginBottom: 12 }}>
            <label style={{
                display: 'block', fontSize: 12, fontWeight: 500,
                color: 'var(--color-text-secondary)', marginBottom: 4,
            }}>
                {label}
            </label>
            {children}
        </div>
    );
}
